import React, { useState, useEffect, useMemo, useRef, useCallback, startTransition } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import entryGuideService from '../../services/entryGuideService';
import Toast from '../../components/Common/Toast';
import FormLoadingSpinner from '../../components/Common/FormLoadingSpinner';
import {
  STOCK_QUALITY_COLUMNS,
  countedSimpleToByCondition,
  convertRowsCountMode,
  mergeAdjustmentRowsFromBulk,
  buildAdjustmentSubmitRows,
  formatAdjustmentSummaryMessage,
  adjustmentSummaryToastType,
  isMissingCatalogProduct,
} from '../../utils/stockByConditionDisplay';
import {
  exportAdjustmentGuideExcel,
  parseAdjustmentCountedImport,
} from '../../utils/adjustmentGuideExcel';
import AdjustmentGuideItemsPanel from './AdjustmentGuideItemsPanel';
import { normalizeTimeForApi } from '../../utils/dateUtils';

const num = (v) => {
  const x = parseFloat(v);
  return Number.isFinite(x) ? x : 0;
};

const EditAdjustmentGuide = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const [loading, setLoading] = useState(true);
  const [stockLoading, setStockLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [warehouseId, setWarehouseId] = useState(null);
  const [suppliers, setSuppliers] = useState([]);

  const [supplierId, setSupplierId] = useState('');
  const [issueDate, setIssueDate] = useState('');
  const [entryDate, setEntryDate] = useState('');
  const [entryTime, setEntryTime] = useState('');
  const [notes, setNotes] = useState('');
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState([]);
  const [adjustmentMode, setAdjustmentMode] = useState('simple');
  const [excelBusy, setExcelBusy] = useState(false);
  const importExcelRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [formRes, guideRes] = await Promise.all([
          entryGuideService.getFormData(),
          entryGuideService.getEntryGuide(id),
        ]);
        if (cancelled) return;
        if (formRes.success && formRes.data?.suppliers) {
          setSuppliers(formRes.data.suppliers);
        }
        if (!guideRes.success || !guideRes.data) {
          setToast({ isOpen: true, message: 'Guía no encontrada', type: 'error' });
          setTimeout(() => navigate('/warehouses'), 1500);
          return;
        }
        const g = guideRes.data;
        if (g.guide_type !== 'ajuste') {
          setToast({ isOpen: true, message: 'Esta guía no es de ajuste', type: 'error' });
          setTimeout(() => navigate(`/warehouses/${g.warehouse_id}/documents`), 1500);
          return;
        }
        setWarehouseId(g.warehouse_id);
        setSupplierId(g.supplier_id ? String(g.supplier_id) : '');
        setIssueDate(g.issue_date?.slice(0, 10) || '');
        setEntryDate(g.entry_date?.slice(0, 10) || '');
        setEntryTime(g.entry_time ? String(g.entry_time).slice(0, 5) : '');
        setNotes(g.notes || '');
        const guideItems = g.items || [];
        const isCostGuide = guideItems.some((it) => it.kardex_event === 'ajuste_costo');
        if (isCostGuide) {
          setAdjustmentMode('cost_only');
        }
        const byPid = new Map();
        for (const it of guideItems) {
          const pid = it.product_id;
          if (!byPid.has(pid)) {
            byPid.set(pid, {
              product_id: pid,
              sku: it.product?.sku || '',
              name: it.product?.name || it.description || '',
              unit_of_measure: it.unit_of_measure || it.product?.unit_of_measure || 'UN',
              stockAtEntry: null,
              stockByCondition: null,
              averageCost: 0,
              averageCostManual: null,
              targetCostManual: isCostGuide ? String(it.unit_price ?? '0') : null,
              countedByCondition: Object.fromEntries(
                STOCK_QUALITY_COLUMNS.map(({ key }) => [key, '0'])
              ),
            });
          }
          const row = byPid.get(pid);
          if (isCostGuide) {
            row.targetCostManual = String(it.unit_price ?? row.targetCostManual ?? '0');
          } else {
            const cond = it.condition || 'good';
            if (STOCK_QUALITY_COLUMNS.some((c) => c.key === cond)) {
              row.countedByCondition[cond] = String(it.quantity ?? '0');
            }
          }
          if (it.product?.sku) row.sku = it.product.sku;
          if (it.product?.name) row.name = it.product.name;
        }
        setRows(Array.from(byPid.values()));
      } catch (e) {
        console.error(e);
        setToast({ isOpen: true, message: 'Error al cargar la guía', type: 'error' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        (r.name && r.name.toLowerCase().includes(q)) ||
        (r.sku && String(r.sku).toLowerCase().includes(q))
    );
  }, [rows, search]);

  const updateRow = useCallback((productId, field, value) => {
    setRows((prev) =>
      prev.map((r) => (r.product_id === productId ? { ...r, [field]: value } : r))
    );
  }, []);

  const updateRowCountedCond = useCallback((productId, key, value) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.product_id !== productId) return r;
        const base = r.countedByCondition || {};
        return { ...r, countedByCondition: { ...base, [key]: value } };
      })
    );
  }, []);

  const updateRowCountedSimple = useCallback((productId, value) => {
    setRows((prev) =>
      prev.map((r) =>
        r.product_id === productId
          ? { ...r, countedByCondition: countedSimpleToByCondition(value) }
          : r
      )
    );
  }, []);

  const handleAdjustmentModeChange = useCallback((next) => {
    setAdjustmentMode(next);
    startTransition(() => {
      setRows((prev) => {
        let updated = prev;
        if (next === 'quality' || next === 'simple') {
          updated = convertRowsCountMode(prev);
        } else if (next === 'cost_only') {
          updated = prev.map((r) => {
            if (r.stockAtEntry === null || r.stockAtEntry === undefined) return r;
            if (Math.abs(num(r.stockAtEntry)) < 0.0001) return r;
            const costStr =
              r.targetCostManual !== null && r.targetCostManual !== undefined && String(r.targetCostManual).trim() !== ''
                ? String(r.targetCostManual)
                : String(num(r.averageCost));
            return { ...r, targetCostManual: costStr };
          });
        }
        return updated;
      });
    });
  }, []);

  const handleExportExcel = async () => {
    if (rows.length === 0 || !warehouseId) return;
    setExcelBusy(true);
    try {
      await exportAdjustmentGuideExcel(
        rows,
        `Ajuste_guia_${id}_almacen_${warehouseId}`,
        { simpleMode: adjustmentMode === 'simple' }
      );
      setToast({
        isOpen: true,
        message: adjustmentMode === 'quality'
          ? `Excel generado (${rows.length} productos). Edite columnas Conteo_* y reimporte si lo desea.`
          : `Excel generado (${rows.length} productos). Edite la columna Conteo_total y reimporte si lo desea.`,
        type: 'success',
      });
    } catch (e) {
      console.error(e);
      setToast({ isOpen: true, message: 'No se pudo generar el Excel.', type: 'error' });
    } finally {
      setExcelBusy(false);
    }
  };

  const handleImportExcel = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setExcelBusy(true);
    try {
      const buf = await file.arrayBuffer();
      const { updates } = await parseAdjustmentCountedImport(buf, { simpleMode: adjustmentMode === 'simple' });
      if (updates.length === 0) {
        setToast({
          isOpen: true,
          message: adjustmentMode === 'quality'
            ? 'No hay filas con conteo en el archivo (rellene columnas Conteo_*).'
            : 'No hay filas con conteo en el archivo (rellene la columna Conteo_total).',
          type: 'warning',
        });
        return;
      }
      const byId = new Map(updates.map((u) => [u.productId, u]));
      setRows((prev) =>
        prev.map((r) => {
          const imported = byId.get(r.product_id);
          if (!imported) return r;
          const patch = imported.patch || {};
          const base = r.countedByCondition
            ? { ...r.countedByCondition }
            : Object.fromEntries(STOCK_QUALITY_COLUMNS.map(({ key }) => [key, '']));
          for (const [k, v] of Object.entries(patch)) {
            base[k] = v;
          }
          const editableCost =
            r.stockAtEntry !== null &&
            r.stockAtEntry !== undefined &&
            Math.abs(num(r.stockAtEntry)) < 0.0001 &&
            imported.averageCostManual !== null;
          return {
            ...r,
            countedByCondition: base,
            averageCostManual: editableCost ? imported.averageCostManual : r.averageCostManual,
          };
        })
      );
      setToast({
        isOpen: true,
        message: `Conteo físico importado. El costo se aplicó solo en productos editables (stock 0).`,
        type: 'success',
      });
    } catch (err) {
      console.error(err);
      setToast({
        isOpen: true,
        message: err?.message || 'No se pudo leer el Excel. Use un archivo exportado desde esta pantalla.',
        type: 'error',
      });
    } finally {
      setExcelBusy(false);
    }
  };

  const handleLoadStock = async () => {
    if (!warehouseId || !entryDate) {
      setToast({ isOpen: true, message: 'Indique la fecha de ingreso', type: 'warning' });
      return;
    }
    const timeForApi = normalizeTimeForApi(entryTime);
    setStockLoading(true);
    try {
      const res = await entryGuideService.getProductStockAndCostBulk(
        warehouseId,
        parseInt(id, 10),
        entryDate,
        timeForApi,
        false
      );
      if (!res.success) {
        setToast({ isOpen: true, message: res.message || 'Error al cargar stock', type: 'error' });
        return;
      }
      const data = res.data || {};
      startTransition(() => {
        setRows((prev) => mergeAdjustmentRowsFromBulk(prev, data, { adjustmentMode }));
      });
      setToast({
        isOpen: true,
        message: `Stock de referencia cargado (${entryDate} ${timeForApi}), excluyendo esta guía.`,
        type: 'success',
      });
    } catch (e) {
      console.error(e);
      setToast({
        isOpen: true,
        message: e.response?.data?.message || 'Error al cargar stock',
        type: 'error',
      });
    } finally {
      setStockLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!supplierId) {
      setToast({ isOpen: true, message: 'Seleccione la entidad (proveedor)', type: 'warning' });
      return;
    }
    const items = buildAdjustmentSubmitRows(rows, adjustmentMode);

    if (items.length === 0) {
      setToast({
        isOpen: true,
        message:
          adjustmentMode === 'cost_only'
            ? 'No hay productos con diferencia de costo para ajustar.'
            : 'No hay productos con diferencia de conteo para ajustar.',
        type: 'warning',
      });
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        adjustment_mode: adjustmentMode,
        supplier_id: parseInt(supplierId, 10),
        issue_date: issueDate,
        entry_date: entryDate,
        entry_time: normalizeTimeForApi(entryTime, { endOfDayIfEmpty: false }),
        notes: notes || null,
        items,
      };
      const debug68 = items.filter((it) => Number(it.product_id) === 68);
      if (debug68.length > 0) {
        console.log('[AJUSTE-DEBUG-68][FRONT][EDIT][payload]', {
          guide_id: id,
          warehouse_id: warehouseId,
          entry_date: entryDate,
          entry_time: normalizeTimeForApi(entryTime, { endOfDayIfEmpty: false }),
          items_68: debug68,
        });
      }
      const res = await entryGuideService.updateEntryGuide(id, payload);
      if (res.success) {
        setToast({
          isOpen: true,
          message: formatAdjustmentSummaryMessage(res),
          type: adjustmentSummaryToastType(res),
        });
        setTimeout(() => navigate(`/warehouses/${warehouseId}/documents`), 1200);
      } else {
        setToast({ isOpen: true, message: res.message || 'No se pudo guardar', type: 'error' });
      }
    } catch (err) {
      const msg =
        formatAdjustmentSummaryMessage(err.response?.data) ||
        err.response?.data?.message ||
        (err.response?.data?.errors && JSON.stringify(err.response.data.errors)) ||
        'Error al guardar';
      setToast({ isOpen: true, message: msg, type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="max-w-[110rem] mx-auto px-1">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-800">Editar guía de ajuste</h1>
          </div>
          <FormLoadingSpinner message="Cargando formulario…" hint="Preparando productos y datos de la guía" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-[110rem] mx-auto px-1">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800">Editar guía de ajuste</h1>
          <p className="text-gray-600 mt-1">
            Actualice entidad, fechas de ingreso o conteos. Use <strong>Cargar stock</strong> para ver el inventario del
            sistema a la fecha/hora de ingreso. Puede contar <strong>por calidad</strong>, usar{' '}
            <strong>ajuste simple</strong> o <strong>solo costos</strong>.
          </p>
        </div>

        <form onSubmit={handleSubmit} noValidate className="space-y-6">
          <div className="bg-white rounded-lg shadow p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Entidad (proveedor)</label>
              <select
                className="w-full border rounded-md px-3 py-2 bg-white"
                value={supplierId}
                onChange={(e) => setSupplierId(e.target.value)}
                required
              >
                <option value="">— Seleccionar —</option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha emisión</label>
              <input
                type="date"
                className="w-full border rounded-md px-3 py-2"
                value={issueDate}
                onChange={(e) => setIssueDate(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha ingreso</label>
              <input
                type="date"
                className="w-full border rounded-md px-3 py-2"
                value={entryDate}
                onChange={(e) => setEntryDate(e.target.value)}
                required
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="block text-sm font-medium text-gray-700">Hora ingreso</label>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="time"
                  className="border rounded-md px-3 py-2 flex-1 min-w-[120px]"
                  value={entryTime}
                  onChange={(e) => setEntryTime(e.target.value)}
                />
                <button
                  type="button"
                  onClick={handleLoadStock}
                  disabled={stockLoading || !warehouseId}
                  className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50 whitespace-nowrap"
                >
                  {stockLoading ? 'Cargando…' : 'Cargar stock'}
                </button>
              </div>
            </div>
            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
              <textarea
                className="w-full border rounded-md px-3 py-2"
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          </div>

          <AdjustmentGuideItemsPanel
            search={search}
            onSearchChange={setSearch}
            filteredRows={filteredRows}
            totalRows={rows.length}
            countLabel="ítems"
            adjustmentMode={adjustmentMode}
            onAdjustmentModeChange={handleAdjustmentModeChange}
            excelBusy={excelBusy}
            onExportExcel={handleExportExcel}
            importExcelRef={importExcelRef}
            onImportExcel={handleImportExcel}
            exportDisabled={rows.length === 0 || !warehouseId}
            onUpdateRow={updateRow}
            onUpdateRowCountedCond={updateRowCountedCond}
            onUpdateRowCountedSimple={updateRowCountedSimple}
          />

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={submitting}
              className="px-6 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50"
            >
              {submitting ? 'Guardando…' : 'Guardar cambios'}
            </button>
            <button
              type="button"
              onClick={() => navigate(`/warehouses/${warehouseId}/documents`)}
              className="px-6 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
            >
              Volver
            </button>
          </div>
        </form>

        <Toast
          isOpen={toast.isOpen}
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, isOpen: false })}
        />
      </div>
    </div>
  );
};

export default EditAdjustmentGuide;
